/* One controller for every editorial film; source files stay inert until playback. */
(() => {
  'use strict';
  if (customElements.get('junior-ambient-video')) return;
  const assetBase = document.currentScript?.src;
  const mobileFilm = assetBase ? new URL('gp-junior-mobile-640-012aa36bc984.mp4', assetBase).href : '';
  const mobileFilmId = '45d34d3c4fe044ed913c9f08ba944e3b';
  customElements.define('junior-ambient-video', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.childrenObserver?.disconnect();
      this.video = this.querySelector('video');
      this.button = this.querySelector('.jr-video-toggle');
      if (!this.video || !this.button) {
        this.childrenObserver = new MutationObserver(() => this.connectedCallback());
        this.childrenObserver.observe(this, {childList:true, subtree:true});
        return;
      }
      this.initialized = true;
      this.setAttribute('role', 'button');
      this.tabIndex = 0;
      const descriptionId = this.video.getAttribute('aria-describedby');
      if (descriptionId) this.setAttribute('aria-describedby', descriptionId);
      this.visible = false;
      this.userPaused ??= false;
      this.explicitPlay = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.viewport = matchMedia('(max-width: 749px)');
      this.allowed = !this.hasAttribute('data-priority');
      this.updateControl = () => {
        const paused = this.video.paused || this.video.ended;
        const label = paused ? this.button.dataset.play : this.button.dataset.pause;
        this.button.dataset.state = paused ? 'paused' : 'playing';
        this.button.querySelector('span').textContent = label;
        this.setAttribute('aria-label', `${label}: ${this.video.getAttribute('aria-label')}`);
        this.setAttribute('aria-pressed', String(!paused));
      };
      this.selectVideo = () => {
        const mobile = this.viewport.matches && this.querySelector('[data-mobile-video-sources]');
        const template = mobile || this.querySelector('[data-desktop-video-sources]');
        const candidates = Array.from(template.content.querySelectorAll('source'));
        const mp4 = candidates.filter(source => source.type === 'video/mp4').sort((a,b) => Number(a.dataset.width)-Number(b.dataset.width));
        const desired = Math.max(480, this.clientWidth * Math.min(devicePixelRatio || 1, 1.5));
        const adaptive = candidates.find(source => /mpegurl/i.test(source.type) && this.video.canPlayType(source.type));
        let source = adaptive || mp4.find(source => Number(source.dataset.width) >= desired) || mp4.at(-1) || candidates[0];
        // Only this selected film has a reviewed square mobile derivative.
        // A different editor selection continues to use its own Shopify sources.
        if (mobile && mobileFilm && source?.src.includes(mobileFilmId)) {
          source = document.createElement('source');
          source.src = mobileFilm;
          source.type = 'video/mp4';
        }
        if (!source || this.video.dataset.source === source.src) return;
        this.video.pause();
        this.video.replaceChildren(source.cloneNode(true));
        this.video.dataset.source = source.src;
        this.video.load();
      };
      this.sync = () => {
        const saveData = navigator.connection?.saveData;
        const mobileNeedsTap = this.hasAttribute('data-mobile-tap-to-play') && this.viewport.matches;
        if (!this.visible || this.userPaused || document.hidden || (!this.explicitPlay && (mobileNeedsTap || this.motion.matches || saveData || !this.allowed))) {
          this.video.pause();
        } else if (!this.video.ended) {
          this.selectVideo();
          this.video.muted = true;
          this.video.play().catch(this.updateControl);
        }
        this.updateControl();
      };
      this.toggle = () => {
        if (!this.video.paused && !this.video.ended) {
          this.userPaused = true;
          this.explicitPlay = false;
        } else {
          this.userPaused = false;
          this.explicitPlay = true;
          if (this.video.ended) this.video.currentTime = 0;
        }
        this.sync();
      };
      this.onMotionChange = () => { this.explicitPlay = false; this.sync(); };
      this.onViewportChange = () => {
        this.video.pause();
        delete this.video.dataset.source;
        if (this.hasAttribute('data-mobile-tap-to-play') && this.viewport.matches && !this.explicitPlay) {
          this.video.replaceChildren();
          this.video.load();
        }
        this.sync();
      };
      this.onPlaying = () => this.setAttribute('data-ready', '');
      this.onEmptied = () => this.removeAttribute('data-ready');
      this.posterImage = this.querySelector('.jr-video-poster img');
      this.onPageLoaded = () => {
        // Let the initial page render before an above-fold film competes for bandwidth.
        this.startFrame = requestAnimationFrame(() => {
          this.startFrame = requestAnimationFrame(() => {
            this.startFrame = null;
            this.allowed = true;
            this.sync();
          });
        });
      };
      this.onControlKeydown = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.toggle();
      };
      this.addEventListener('click', this.toggle);
      this.addEventListener('keydown', this.onControlKeydown);
      ['play','pause','ended'].forEach(event => this.video.addEventListener(event, this.updateControl));
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('emptied', this.onEmptied);
      this.motion.addEventListener('change', this.onMotionChange);
      this.viewport.addEventListener('change', this.onViewportChange);
      document.addEventListener('visibilitychange', this.sync);
      if (!this.allowed) {
        if (document.readyState === 'complete') this.onPageLoaded();
        else window.addEventListener('load', this.onPageLoaded, {once:true});
      }
      this.button.hidden = true;
      this.updateControl();
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
        this.sync();
      }, {threshold:[0,0.25]});
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.childrenObserver?.disconnect();
      if (this.startFrame != null) cancelAnimationFrame(this.startFrame);
      window.removeEventListener('load', this.onPageLoaded);
      this.video?.pause();
      this.observer?.disconnect();
      this.motion?.removeEventListener('change', this.onMotionChange);
      this.viewport?.removeEventListener('change', this.onViewportChange);
      document.removeEventListener('visibilitychange', this.sync);
      this.removeEventListener('click', this.toggle);
      this.removeEventListener('keydown', this.onControlKeydown);
      ['play','pause','ended'].forEach(event => this.video?.removeEventListener(event, this.updateControl));
      this.video?.removeEventListener('playing', this.onPlaying);
      this.video?.removeEventListener('emptied', this.onEmptied);
      this.initialized = false;
    }
  });
})();
